import { Avatar, Modal } from "antd";
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useMyContext } from "../../../context/MyContext";
import { api } from "../../../config/api";
import dayjs from "dayjs";
import { Loader2, Paperclip, Send, X, Check, Download, FileText } from "lucide-react";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, storage } from "../../../config/firebase";
import { AiOutlineUser } from "react-icons/ai";
import { API_BASE_URL } from "../../../config/api"
import { io } from "socket.io-client";
import { signInAnonymously } from "firebase/auth";

const getSocketBaseUrl = () => {
  const envUrl = process.env.REACT_APP_SOCKET_URL;

  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const isLocalhost =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "::1";

    if (isLocalhost) {
      return process.env.REACT_APP_SOCKET_URL_LOCAL || "http://localhost:8800";
    }
  }

  return envUrl || "https://server.oneunionsolutions.com/";
};

const ensureStorageAuth = async () => {
  if (auth.currentUser) {
    return auth.currentUser;
  }

  try {
    const credential = await signInAnonymously(auth);
    return credential.user;
  } catch (error) {
    throw new Error(error?.message || "Unable to authenticate file upload");
  }
};

const AskForEstimate = ({
  id,
  estimateId,
  proposalId,
  guestUserName = "",
  guestUserEmail = "",
  presenceUserId,
  notificationLink,
  notificationType = "est",
  containerClassName = "h-[60vh]",
  fetchCommentsUrl,
  addCommentUrl,
  syncCommentsUrl,
  attachmentUploadUrl,
  disableFrontendNotifications = false,
  markNotificationsAsReadOnOpen = true,
  showPresenceStatus = true,
  isGuestSender = false,
}) => {
  const { user, onlineUsers } = useMyContext();
  const chatBoxRef = useRef(null);
  const queryClient = useQueryClient();
  const guestSocketRef = useRef(null);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const attachmentPreviewObjectUrlRef = useRef(null);
  const [guestOnlineUsers, setGuestOnlineUsers] = useState([]);
  const isSubmitting = useRef(false);
  const isGuestSession = !user?.uid || isGuestSender;

  const optionsArray = useMemo(() => [
    "super admin",
    "managing admin",
    "lead admin",
    "editor",
  ], []);

  const currentSenderName = useMemo(() => {
    if (isGuestSender) {
      return guestUserName || "Guest User";
    }
    if (optionsArray.includes(user?.role)) return "admin";
    if (user?.name) return user.name;
    return guestUserName || "Guest User";
  }, [guestUserName, isGuestSender, user?.name, user?.role, optionsArray]);

  const [formData, setFormData] = useState({
    user: currentSenderName,
    message: "",
  });
  const adminRoles = useMemo(() => (
    ["super admin", "managing admin", "lead admin", "editor"]
  ), []);
  const isAdmin = adminRoles.includes(user?.role);
  const resolvedPresenceUserId =
    presenceUserId || guestUserEmail || guestUserName || `guest-estimate-${estimateId}`;
  const effectiveOnlineUsers = isGuestSession ? guestOnlineUsers : onlineUsers;
  const resolvedNotificationLink =
    notificationLink || `/newRequest?tab=external-estimates&estimateId=${estimateId}&openChat=true`;
  const resolvedFetchCommentsUrl =
    fetchCommentsUrl || `${API_BASE_URL}/clientestimates/getClientEstimateComments?estimateId=${estimateId}`;
  const resolvedAddCommentUrl =
    addCommentUrl || `${API_BASE_URL}/clientestimates/addClientEstimateComments`;
  const resolvedSyncCommentsUrl =
    syncCommentsUrl || `${API_BASE_URL}/clientestimates/updateClientEstimateComments`;

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      user: currentSenderName,
    }));
  }, [currentSenderName]);

  const { data } = useQuery(
    ["estimate", id],
    async () => {
      const response = await api.get(resolvedFetchCommentsUrl);
      const estimateData = response.data.data;
      return {
        ...estimateData,
        comments: estimateData.comments || []
      };
    },
    {
      refetchInterval: 2000,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,
    }
  );

  useEffect(() => {
    if (!isGuestSession) return undefined;

    const socket = io(getSocketBaseUrl(), { autoConnect: true });
    guestSocketRef.current = socket;

    const emitGuestOnline = () => {
      socket.emit("status", {
        userId: resolvedPresenceUserId,
        status: true,
        role: "external-guest",
        name: guestUserName || "Guest User",
      });
    };

    socket.on("status-listner", (nextOnlineUsers) => {
      setGuestOnlineUsers(Array.isArray(nextOnlineUsers) ? nextOnlineUsers : []);
    });
    socket.on("connect", emitGuestOnline);

    if (socket.connected) {
      emitGuestOnline();
    }

    const emitGuestOffline = () => {
      try {
        socket.emit("logout", resolvedPresenceUserId);
      } catch (_error) {
        // best effort only
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", emitGuestOffline);
      window.addEventListener("pagehide", emitGuestOffline);
    }

    if (typeof document !== "undefined") {
      const onVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
          emitGuestOffline();
        } else if (document.visibilityState === "visible" && socket.connected) {
          emitGuestOnline();
        }
      };

      document.addEventListener("visibilitychange", onVisibilityChange);

      return () => {
        emitGuestOffline();
        document.removeEventListener("visibilitychange", onVisibilityChange);
        if (typeof window !== "undefined") {
          window.removeEventListener("beforeunload", emitGuestOffline);
          window.removeEventListener("pagehide", emitGuestOffline);
        }
        socket.off("status-listner");
        socket.off("connect", emitGuestOnline);
        socket.disconnect();
        guestSocketRef.current = null;
      };
    }

    return () => {
      emitGuestOffline();
      if (typeof window !== "undefined") {
        window.removeEventListener("beforeunload", emitGuestOffline);
        window.removeEventListener("pagehide", emitGuestOffline);
      }
      socket.off("status-listner");
      socket.off("connect", emitGuestOnline);
      socket.disconnect();
      guestSocketRef.current = null;
    };
  }, [guestUserName, isGuestSession, resolvedPresenceUserId]);
  
  // Double tick for read state, visually consistent with ticket chat
  const DoubleCheck = ({ size = 14, colorClass = "text-sky-300" }) => {
    const offset = Math.round(size * 0.45);
    return (
      <span className="relative inline-block align-middle" style={{ width: size + offset, height: size }}>
        <Check size={size} className={`${colorClass}`} style={{ position: 'absolute', top: 0, left: 0 }} />
        <Check size={size} className={`${colorClass}`} style={{ position: 'absolute', top: 0, left: offset }} />
      </span>
    );
  };

  // Helpers for attachment rendering
  const getFileNameFromUrl = (url) => decodeURIComponent((url || '').split('/').pop() || 'attachment');
  const isPureUrl = (txt) => typeof txt === 'string' && /^https?:\/\/\S+$/i.test(txt.trim());
  const getAttachmentMeta = (c) => {
    if (!c) return null;
    const url = c.fileUrl || (isPureUrl(c.message) ? c.message.trim() : null);
    if (!url) return null;
    return {
      url,
      fileName: c.fileName || getFileNameFromUrl(url),
      extension: (c.fileName || getFileNameFromUrl(url)).split('.').pop()?.toLowerCase() || 'file',
      pureMessageUrl: isPureUrl(c.message),
    };
  };
  const getAttachmentTone = (ext) => {
    if (ext === 'pdf') return 'bg-red-100 text-red-700';
    if (ext === 'xls' || ext === 'xlsx') return 'bg-green-100 text-green-700';
    if (ext === 'csv') return 'bg-emerald-100 text-emerald-700';
    return 'bg-slate-100 text-slate-700';
  };
  const downloadAttachment = async (url, fileName) => {
    try {
      const response = await fetch(`/api/tracking/download-attachment?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(fileName || 'attachment')}`);
      if (!response.ok) {
        let detail = '';
        try {
          detail = await response.clone().text();
        } catch (_error) {
          detail = 'Unable to read error response';
        }
        console.error('External estimate attachment download failed:', {
          status: response.status,
          statusText: response.statusText,
          url,
          fileName,
          detail,
        });
        throw new Error('Failed');
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('External estimate attachment download error:', error);
      toast.error('Unable to download attachment. Please try again.');
    }
  };

  const closeAttachmentPreview = useCallback(() => {
    if (attachmentPreviewObjectUrlRef.current) {
      window.URL.revokeObjectURL(attachmentPreviewObjectUrlRef.current);
      attachmentPreviewObjectUrlRef.current = null;
    }
    setAttachmentPreview(null);
  }, []);

  const openAttachmentPreview = useCallback(async (url, type, fileName) => {
    try {
      const response = await api.get("/tracking/download-attachment", {
        params: {
          url,
          filename: fileName || "attachment",
        },
        responseType: "blob",
      });

      if (attachmentPreviewObjectUrlRef.current) {
        window.URL.revokeObjectURL(attachmentPreviewObjectUrlRef.current);
      }

      const objectUrl = window.URL.createObjectURL(response.data);
      attachmentPreviewObjectUrlRef.current = objectUrl;

      setAttachmentPreview({
        type,
        url: objectUrl,
        fileName: fileName || "attachment",
      });
    } catch (error) {
      console.error("Attachment preview failed:", {
        url,
        type,
        fileName,
        message: error?.message || error,
      });
      toast.error("Unable to preview attachment. Please try again.");
    }
  }, []);

  const handleAttachmentClick = useCallback((url, extension, fileName) => {
    const ext = (extension || "").toLowerCase();
    const imageTypes = ["png", "jpg", "jpeg", "gif", "webp"];
    if (ext === "pdf") {
      openAttachmentPreview(url, "pdf", fileName);
      return;
    }
    if (imageTypes.includes(ext)) {
      openAttachmentPreview(url, "image", fileName);
      return;
    }
    downloadAttachment(url, fileName);
  }, [openAttachmentPreview]);

  useEffect(() => () => {
    if (attachmentPreviewObjectUrlRef.current) {
      window.URL.revokeObjectURL(attachmentPreviewObjectUrlRef.current);
      attachmentPreviewObjectUrlRef.current = null;
    }
  }, []);

  const isOwnMessage = useCallback(
    (c) => {
      const sender = (c?.user || "").toLowerCase();
      const viewerIsAdmin = !isGuestSender && optionsArray.includes(user?.role);
      if (viewerIsAdmin) {
        return sender === "admin";
      } else {
        return sender !== "admin";
      }
    },
    [user?.role, optionsArray, isGuestSender]
  );

  // Mark incoming messages as delivered/read (persist via API)
  const markDeliveryAndRead = useCallback(async () => {
    try {
      if (!Array.isArray(data?.comments) || data.comments.length === 0) return;
      const now = new Date().toISOString();
      let changed = false;
      const updated = data.comments.map((c) => {
        const isOwn = isOwnMessage(c);
        if (isOwn) return c; // Only acknowledge incoming items
        const next = { ...c };
        if (!next.status || next.status === 'sent') {
          next.status = 'delivered';
          next.deliveredAt = next.deliveredAt || now;
          changed = true;
        }
        if ((document?.visibilityState === 'visible') && next.status !== 'read') {
          next.status = 'read';
          next.readAt = now;
          changed = true;
        }
        return next;
      });
      if (changed) {
        await api.put(resolvedSyncCommentsUrl, {
          estimateId,
          comments: updated,
        });
      }
    } catch (e) {
      // Non-blocking
      // console.log('Ack error (estimate comments):', e?.message);
    }
  }, [data?.comments, estimateId, isOwnMessage, resolvedSyncCommentsUrl]);

  useEffect(() => {
    markDeliveryAndRead();
  }, [markDeliveryAndRead]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') markDeliveryAndRead();
    };
    const onFocus = () => markDeliveryAndRead();
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
    if (typeof window !== 'undefined') window.addEventListener('focus', onFocus);
    return () => {
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
      if (typeof window !== 'undefined') window.removeEventListener('focus', onFocus);
    };
  }, [markDeliveryAndRead]);

  const mutation = useMutation(
    async (newComment) => {
      const response = await api.put(resolvedAddCommentUrl, { 
        estimateId: estimateId, 
        message: newComment.message,
        user: currentSenderName,
        clientId: newComment.clientId,
        fileUrl: newComment.fileUrl,
        fileName: newComment.fileName,
        fileAttachmentId: newComment.fileAttachmentId,
      });
      return response.data.data; // Return comments from the updated response structure
    },
    {
      onSuccess: (updatedComments) => {
        queryClient.setQueryData(["estimate", id], (oldData) => ({
          ...oldData,
          comments: updatedComments,
        }));
        // No refetch here; cache is already updated
      },
    }
  );
  
  // Smooth auto-scroll to latest message
  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTo({
        top: chatBoxRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [data?.comments]);

  const counterpartyName = useMemo(() => {
    if (!isAdmin) return "Admin";
    return data?.userName || guestUserName || data?.user?.name || "User";
  }, [data, guestUserName, isAdmin]);

  const isOnline = useMemo(() => {
    if (!effectiveOnlineUsers) return false;
    if (!isAdmin) {
      return effectiveOnlineUsers.some((u) => adminRoles.includes(u.role) && u.isOnline);
    }
    const targetUserId = data?.userId || resolvedPresenceUserId;
    if (!targetUserId) return false;
    return effectiveOnlineUsers.some((u) => (u.userId === targetUserId) && u.isOnline);
  }, [effectiveOnlineUsers, isAdmin, data, adminRoles, resolvedPresenceUserId]);

  useEffect(() => {
    if (!markNotificationsAsReadOnOpen) return;

    const markNotificationsAsRead = async () => {
      if (estimateId && user?.uid) {
        try {
          await api.put(`/notifications/est/${user.uid}`, {
            role: optionsArray.includes(user?.role) ? "admin" : user?.role,
            quoteNumber: estimateId,
          });
          queryClient.invalidateQueries(["notifications"]);
        } catch (error) { 
          console.error("Error marking notifications as read:", error);
        }
      }
    };

    markNotificationsAsRead();
  }, [estimateId, markNotificationsAsReadOnOpen, user?.uid, user?.role, queryClient]);

  const handleAddData = useCallback(
    async (e) => {  
      if (e) e.preventDefault();
      if (isSubmitting.current) return; 
      if (!formData.message.trim() && !file) {
        toast.error("Please add a message or attach a file.");
        return;
      }
      setLoading(true);
      isSubmitting.current = true;
      let finalMessage = formData.message.trim();
      try {
        if (file) {
          let fileUrl = "";
          let uploadedFileName = file.name;
          let uploadedFileAttachmentId = "";

          if (attachmentUploadUrl) {
            const uploadFormData = new FormData();
            uploadFormData.append("file", file);
            const uploadResponse = await api.post(attachmentUploadUrl, uploadFormData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            });
            fileUrl = uploadResponse?.data?.data?.fileUrl || "";
            uploadedFileName = uploadResponse?.data?.data?.fileName || file.name;
            uploadedFileAttachmentId = uploadResponse?.data?.data?.fileAttachmentId || "";
          } else {
            await ensureStorageAuth();
            const safeName = `${Date.now()}_${file.name}`.replace(/[^\w.\-]/g, "_");
            const path = `/ticket_chat_attachments/${safeName}`;
            const fileRef = ref(storage, path);
            await uploadBytes(fileRef, file);
            fileUrl = await getDownloadURL(fileRef);
          }

          if (!fileUrl) {
            throw new Error("Attachment upload failed");
          }

          // Keep message as text only; store attachment separately
          const clientId = (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? crypto.randomUUID()
            : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

          const messageData = {
            ...formData,
            message: finalMessage,
            date: dayjs().format("DD MMM YYYY hh:mm A"),
            clientId,
            fileUrl,
            fileName: uploadedFileName,
            fileAttachmentId: uploadedFileAttachmentId,
          };

          setFormData((prevFormData) => ({ ...prevFormData, message: "" }));
          setFile(null);

          // Optimistic UI
          queryClient.setQueryData(["estimate", id], (old) => ({
            ...(old || {}),
            comments: [ ...((old && old.comments) || []), messageData ],
          }));
          await mutation.mutateAsync(messageData);

          // Notifications (non-blocking)
          if (!disableFrontendNotifications && optionsArray.includes(user?.role)) {
            if (data?.userId && data?.userId !== user?.uid) {
              api.post("/notifications", {
                userId: data?.userId,
                title: "New Comment",
                message: messageData?.message || `Attachment: ${uploadedFileName}`,
                body: messageData?.message || `Attachment: ${uploadedFileName}`,
                read: false,
                type: notificationType,
                link: "/bookshipment",
                linkText: "See Quote",
                date: dayjs().format("DD MMM YYYY hh:mm A"),
                role: "user",
                refId: id,
                quoteNumber: estimateId.toString(),
                sender: user?.uid
              }).catch(() => {});
            }
          } else if (!disableFrontendNotifications) {
            api.post("/notifications", {
              userId: null,
              title: "New Comment",
              message: messageData?.message || `Attachment: ${uploadedFileName}`,
              body: messageData?.message || `Attachment: ${uploadedFileName}`,
              read: false,
              type: notificationType,
              link: resolvedNotificationLink,
              linkText: "See Quote",
              date: dayjs().format("DD MMM YYYY hh:mm A"),
              role: "admin",
              refId: id,
              quoteNumber: estimateId.toString(),
              sender: user?.uid,
              guestUserName,
              guestUserEmail,
            }).catch(() => {});
          }

          return; // attachment path handled; exit here
        }

        const clientId = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

        const messageData = {
          ...formData,
          message: finalMessage,
          date: dayjs().format("DD MMM YYYY hh:mm A"),
          clientId,
        };

        setFormData((prevFormData) => ({ ...prevFormData, message: "" }));
        setFile(null);
        
        
        // Optimistic UI: append immediately to cache, then persist in background
        queryClient.setQueryData(["estimate", id], (old) => ({
          ...(old || {}),
          comments: [ ...((old && old.comments) || []), messageData ],
        }));
        await mutation.mutateAsync(messageData);
        
        // Don't send notification to yourself
        if (!disableFrontendNotifications && optionsArray.includes(user?.role)) {
          // If user is admin, send notification ONLY to client
          if (data?.userId && data?.userId !== user?.uid) {
            api.post("/notifications", {
              userId: data?.userId,
              title: "New Comment",
              message: messageData?.message,
              body: messageData?.message, // Include both for compatibility
              read: false,
              type: notificationType,
              link: "/bookshipment",
              linkText: "See Quote",
              date: dayjs().format("DD MMM YYYY hh:mm A"),
              role: "user",
              refId: id,
              quoteNumber: estimateId.toString(),
              sender: user?.uid // Add sender ID to filter out self-notifications
            }).catch(() => {});
          }
        } else if (!disableFrontendNotifications) {
          // If user is client, send notification ONLY to admin
          api.post("/notifications", {
            userId: null, // Send to all admins
            title: "New Comment",
            message: messageData?.message, // Use message instead of body
            body: messageData?.message, // Include both for compatibility
            read: false,
            type: notificationType,
            link: resolvedNotificationLink,
            linkText: "See Quote",
            date: dayjs().format("DD MMM YYYY hh:mm A"),
            role: "admin",
            refId: id,
            quoteNumber: estimateId.toString(),
            sender: user?.uid, // Add sender ID to filter out self-notifications
            guestUserName,
            guestUserEmail,
          }).catch(() => {});
        }
      } catch (error) {
        console.error("Error updating status:", error);
        toast.error(error?.message || "Error updating status. Please try again later.");
      } finally {
        setLoading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setTimeout(() => { isSubmitting.current = false; }, 300);
      }
    },
    [data, disableFrontendNotifications, estimateId, file, formData, guestUserEmail, guestUserName, id, mutation, notificationType, optionsArray, resolvedAddCommentUrl, resolvedNotificationLink, user]
  );

  return (
    <div className={`flex flex-col w-full ${containerClassName}`}>
      {/* Header */}
      <div className="w-full bg-cus-blue py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Avatar icon={<AiOutlineUser />} className="bg-white text-cus-blue" size={36} />
              {showPresenceStatus ? (
                <div className={`absolute -top-1 -right-1 size-3 rounded-full ${isOnline ? "bg-green-500" : "bg-red-500"}`} />
              ) : null}
            </div>
            <div className="flex flex-col items-start text-white">
              <p className="text-base font-medium">{counterpartyName}</p>
              {showPresenceStatus ? (
                <p className="text-[10px]">{isOnline ? "Online" : "Offline"}</p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <div
        ref={chatBoxRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-0"
      >
            {Array.isArray(data?.comments) && data.comments.length > 0 ? (
              (() => {
                const seen = new Set();
                const uniq = data.comments.filter((c) => {
                  const key = c?.clientId || `${c?.user}|${c?.message}|${c?.date}`;
                  if (seen.has(key)) return false;
                  seen.add(key);
                  return true;
                });
                return uniq.map((item, i) => {
                const isOwn = isOwnMessage(item);
                const derivedStatus = item?.status || 'sent';
                const attachment = getAttachmentMeta(item);
                const hideMessageText = attachment?.pureMessageUrl === true;
                return (
                <div
                  key={i}
                className={`flex ${
                  isOwn
                    ? "justify-end"
                    : "justify-start"
                }`}
                >
                <div className="max-w-[70%]">
                  <div
                    className={`rounded-2xl px-4 py-3 text-sm shadow-sm ${
                      isOwn
                        ? "bg-cus-blue text-white rounded-tr-none"
                        : "bg-gray-200 text-gray-900 rounded-tl-none"
                    }`}
                  >
                    {item?.message && !hideMessageText ? <p>{item?.message}</p> : null}
                    {attachment ? (
                      <button
                        type="button"
                        onClick={() => handleAttachmentClick(attachment.url, attachment.extension, attachment.fileName)}
                        className={`mt-2 w-full inline-flex items-center justify-between gap-2 text-xs rounded-lg px-2 py-1.5 border transition-all ${
                          isOwn
                            ? "bg-white/15 border-white/20 text-white hover:bg-white/25"
                            : "bg-white border-gray-300 text-cus-blue hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="size-6 rounded-md bg-black/10 flex items-center justify-center shrink-0">
                            <FileText size={13} />
                          </div>
                          <span className="max-w-[180px] truncate">{attachment.fileName}</span>
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${getAttachmentTone(attachment.extension)}`}>
                            {(attachment.extension || 'FILE').toUpperCase().slice(0,4)}
                          </span>
                        </div>
                        <div
                          className="size-6 rounded-full bg-black/10 flex items-center justify-center shrink-0"
                          onClick={(e) => { e.stopPropagation(); downloadAttachment(attachment.url, attachment.fileName); }}
                          role="button"
                          aria-label="Download attachment"
                          title="Download"
                        >
                          <Download size={12} />
                        </div>
                      </button>
                    ) : null}
                  </div>
                  <div
                    className={`text-xs text-gray-500 mt-1 ${
                      isOwn ? "text-right" : "text-left"
                    }`}
                  >
                    {(() => {
                      const d = dayjs(item?.date);
                      const formatted = d.isValid()
                        ? d.format('DD MMM YYYY hh:mm A')
                        : (item?.date || "");
                      const label = (typeof item?.user === 'string' && item.user.toLowerCase() === 'admin') ? 'Admin' : (item?.user || '');
                      return `${formatted} • ${label}`;
                    })()}
                    {isOwn ? (
                      <span className="ml-2 inline-flex items-center">
                        {derivedStatus === 'read' ? (
                          <DoubleCheck size={14} colorClass="text-sky-300" />
                        ) : derivedStatus === 'delivered' ? (
                          <DoubleCheck size={14} colorClass="text-gray-400" />
                        ) : (
                          <Check size={14} className="text-gray-400" />
                        )}
                      </span>
                    ) : null}
                  </div>
                </div>
                </div>
              );
            });
          })()
            ) : (
            <div className="text-center text-gray-500 py-8">No comments yet. Start the conversation!</div>
            )}
      </div>

      {/* Input Bar */}
      <div className="border-t border-gray-200 bg-white px-4 py-3 shrink-0">
        <div className="flex items-center gap-3">
          <label htmlFor="estimate-file" className="border rounded-lg p-2 cursor-pointer flex items-center justify-center">
            <Paperclip size={18} />
            <input
              id="estimate-file"
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                setFile(f);
              }}
              accept=".png,.jpg,.jpeg,.pdf,.xlsx,.xls,.csv"
              ref={fileInputRef}
            />
          </label>
          <input
            placeholder="Type your message..."
            name="message"
            onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleAddData(e);
              }
            }}
            value={formData.message}
            className="flex-1 px-4 py-3 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-cus-blue text-sm"
            autoComplete="off"
            autoFocus
          />
          {file && (
            <div className="flex items-center gap-1 text-xs bg-gray-100 rounded px-2 py-1">
              <span className="max-w-[140px] truncate">{file.name}</span>
              <button type="button" className="hover:text-red-500" onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }} aria-label="Remove attachment">
                <X size={14} />
              </button>
            </div>
          )}
          <button
            onClick={handleAddData}
            disabled={!formData.message.trim() && !file || loading}
            className="btnBg text-white p-3 rounded-lg disabled:opacity-50"
          >
            {loading ? <Loader2 className="animate-spin" /> : <Send size={18} />}
          </button>
        </div>
      </div>

      <Modal
        open={Boolean(attachmentPreview)}
        title={attachmentPreview?.fileName || "Attachment Preview"}
        onCancel={closeAttachmentPreview}
        footer={null}
        width="80%"
        style={{ top: 20 }}
        destroyOnClose
      >
        {attachmentPreview?.type === "pdf" ? (
          <iframe
            src={`${attachmentPreview.url}#toolbar=0`}
            title={attachmentPreview.fileName || "Attachment Preview"}
            style={{ width: "100%", height: "70vh", border: 0 }}
          />
        ) : attachmentPreview?.type === "image" ? (
          <div className="flex items-center justify-center min-h-[50vh] bg-slate-50 rounded-lg overflow-hidden">
            <img
              src={attachmentPreview.url}
              alt={attachmentPreview.fileName || "Attachment Preview"}
              className="max-h-[70vh] w-auto object-contain"
            />
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default AskForEstimate;
